import { useReportContext } from "../../../report-context";

import useColumnMutations from "./useColumnMutations";

import { ToastType } from "@/enums";
import { ColumnType, ReportColumn } from "@/types/evaluate";
import { displayToast } from "@/utils/toast";

interface UseColumnSaveHandlersProps {
  type?: "create" | "update";
}

const useColumnSaveHandlers = ({ type }: UseColumnSaveHandlersProps) => {
  const reportContext = useReportContext();
  const { selectedColumn } = reportContext;
  const { saveColumn, isLoading } = useColumnMutations({ type });

  const validateColumnData = (name: string) => {
    if (!selectedColumn) {
      displayToast("No column selected", ToastType.error);
      return false;
    }

    if (!name || name.trim() === "") {
      displayToast("Column name is required", ToastType.error);
      return false;
    }

    return true;
  };

  const createUpdatedColumn = (
    name: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    configuration: any,
  ): ReportColumn => ({
    ...selectedColumn!,
    name: name.trim(),
    configuration,
    column_type: selectedColumn!.column_type || ColumnType.PROMPT_TEMPLATE,
  });

  const handleSave = async ({
    name,
    configuration,
  }: {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    configuration: any;
  }): Promise<void> => {
    if (!validateColumnData(name)) {
      return;
    }

    const updatedColumn = createUpdatedColumn(name, configuration);
    reportContext.setSelectedColumn(updatedColumn);
    saveColumn(updatedColumn).then(() => {
      reportContext.closeSidebar(true);
    });
  };

  const handleAutoSave = async ({
    name,
    configuration,
  }: {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    configuration: any;
  }): Promise<void> => {
    if (!selectedColumn) {
      return;
    }

    const updatedColumn = createUpdatedColumn(name, configuration);
    reportContext.setSelectedColumn(updatedColumn);
    await saveColumn(updatedColumn);
  };

  return {
    handleSave,
    handleAutoSave,
    isLoading,
  };
};

export default useColumnSaveHandlers;
